import {
  Binder,
  LifecycleService,
  EventBinderChangedDetail,
} from '@ribajs/core'
import { getViewportDimensions } from '@ribajs/utils'
import { EventDispatcher } from '@ribajs/events'
import { debounce } from '@ribajs/utils'
import { setShopifyImageSize } from '../utils/index.js'

import type { ShopifyImageSizeOptions } from '../types/index.js'

/**
 * Sets the shopify image source with autodetect the image width
 */
export class ShopifyImageSrcBinder extends Binder<
  string | undefined,
  HTMLImageElement | HTMLVideoElement
> {
  static key = 'shopify-image-*'

  src?: string

  lifecycle = LifecycleService.getInstance()
  events = EventDispatcher.getInstance()
  attributeName: 'src' | 'poster' = 'src'
  options: ShopifyImageSizeOptions = {}

  protected setAttribute(src: string) {
    let oldValue = this.el.getAttribute(this.attributeName)

    if (oldValue && !oldValue?.startsWith('http')) {
      if (oldValue.startsWith('//')) {
        oldValue = window.location.protocol + oldValue
      } else {
        oldValue =
          window.location.protocol + '//' + window.location.host + oldValue
      }
    }

    if (oldValue === src) return

    this.el.setAttribute(this.attributeName, src)
    this.el.dispatchEvent(
      new CustomEvent<EventBinderChangedDetail>('binder-changed', {
        detail: {
          name: this.type,
          newValue: src,
          oldValue,
        },
      }),
    )
  }

  protected removeAttribute() {
    this.el.removeAttribute(this.attributeName)
  }

  async _updateImage() {
    const dpr = window.devicePixelRatio || 1
    const elWidth =
      this.el.clientWidth ||
      this.el.parentElement?.clientWidth ||
      getViewportDimensions().w ||
      0
    const width = elWidth * dpr

    if (!this.src) {
      return
    }

    // in most cases a width of 0 means that the element is not yet visible (for example in the sidebar)
    // So we do nothing and hope that the image is visible in a future check
    if (!width) {
      // console.warn(`[ShopifyImageSrcBinder] Width is ${width}!`, this.el);
      // Use original image size as fallback
      this.setAttribute(this.src)
      return
    }

    this.setAttribute(
      setShopifyImageSize(this.src, width, undefined, this.options),
    )
  }

  updateImage = debounce(this._updateImage.bind(this))

  _removeImage() {
    this.removeAttribute()
  }

  removeImage = this._removeImage.bind(this)

  onReady() {
    this.updateImage()
  }

  onSidebar() {
    this.updateImage()
  }

  parseAttributes(el?: HTMLImageElement | HTMLVideoElement) {
    el ||= this.el

    // Get aspect ratio from element attributes
    let aspectWidth: string | number | null =
      el.getAttribute('data-aspect-width') || el.getAttribute('width')
    let aspectHeight: string | number | null =
      el.getAttribute('data-aspect-height') || el.getAttribute('height')

    const crop = el.getAttribute('crop')

    if (crop) {
      this.options.crop = crop as any
    }

    if (aspectWidth === 'parent') {
      aspectWidth = this.el.clientWidth
    }

    if (aspectHeight === 'parent') {
      aspectHeight = this.el.clientHeight
    }

    if (aspectWidth && typeof aspectWidth === 'string') {
      aspectWidth = parseInt(aspectWidth)
    }

    if (aspectHeight && typeof aspectHeight === 'string') {
      aspectHeight = parseInt(aspectHeight)
    }

    const aspectRatio =
      aspectWidth && aspectHeight
        ? (aspectWidth as number) / (aspectHeight as number)
        : undefined

    if (aspectRatio) {
      this.options.aspectRatio = aspectRatio
    }
  }

  routine(el: HTMLImageElement, src?: string) {
    src ||=
      this.el.getAttribute('src') ||
      this.el.getAttribute('poster') ||
      this.el.getAttribute('data-src') ||
      this.el.getAttribute('data-poster') ||
      undefined
    if (!src) {
      this.removeImage()
      return
    }
    this.src = src
    this.updateImage()
  }

  bind(el: HTMLImageElement) {
    window.addEventListener('resize', this.updateImage, { passive: true })
    this.lifecycle.events.on('ComponentLifecycle:allBound', this.onReady, this)
    this.events.on('sidebar-state-changed', this.onSidebar, this)
    this.attributeName =
      (this.args[0] as 'src' | 'poster') || this.attributeName

    this.parseAttributes(el)
  }

  unbind() {
    window.removeEventListener('resize', this.updateImage)
    this.lifecycle.events.off('ComponentLifecycle:allBound', this.onReady, this)
    this.events.off('sidebar-state-changed', this.onSidebar, this)
  }
}
